import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout, { PortalShell, Breadcrumb } from '@/components/feature/PageShell';
import MediaCard from '@/components/feature/MediaCard';
import FilterBar, { FilterSummary, defaultFilters, type FilterState } from '@/components/feature/FilterBar';
import MobileFilter from '@/components/feature/MobileFilter';
import { mediaList, CATEGORY_GENRES } from '@/mocks/movies';
import { usePageMeta } from '@/hooks/usePageMeta';
import { applyFilters } from '@/pages/category/page';

const hotKeywords = ['悬疑', '治愈', '2026', '科幻', '古装', '纪录片', '日本'];

const allGenres = ['全部', ...Array.from(new Set(Object.values(CATEGORY_GENRES).flat().filter((genre) => genre !== '全部')))];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get('q')?.trim() ?? '';
  const [input, setInput] = useState(keyword);
  const [filters, setFilters] = useState<FilterState>(defaultFilters);

  useEffect(() => {
    setInput(keyword);
    setFilters(defaultFilters);
  }, [keyword]);

  usePageMeta({
    title: keyword ? `“${keyword}”搜索结果 - 菠萝蜜网影视内容` : '影视搜索 - 菠萝蜜网影视内容',
    description: keyword
      ? `菠萝蜜网为您找到与“${keyword}”相关的影视作品，可继续按类型、地区、年份筛选。`
      : '在菠萝蜜网搜索电影、电视剧、动漫、综艺与纪录片，支持按类型、地区、年份筛选。',
    keywords: keyword ? `${keyword},影视搜索,${keyword}推荐` : '影视搜索,电影搜索,电视剧搜索',
    path: `/search${params.toString() ? `?${params.toString()}` : ''}`,
  });

  const matched = useMemo(() => {
    if (!keyword) return [];
    const lower = keyword.toLowerCase();
    return mediaList.filter((item) => {
      const haystack = [item.title, item.enTitle, item.type, item.region, String(item.year), item.director, ...item.cast, ...item.genres, ...item.tags]
        .join(' ')
        .toLowerCase();
      return haystack.includes(lower);
    });
  }, [keyword]);

  const results = useMemo(() => applyFilters(matched, filters), [matched, filters]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const value = input.trim();
    if (!value) return;
    setParams({ q: value });
  };

  const recommendations = [...mediaList].sort((a, b) => b.heat - a.heat).slice(0, 4);

  return (
    <SiteLayout>
      <PortalShell>
        <Breadcrumb items={[{ label: '搜索结果' }]} />

        <section className="rounded-xl border border-background-200 bg-background-100/70 p-4 md:p-6">
          <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">影视搜索</h1>
          <form onSubmit={handleSubmit} className="mt-3 flex flex-col gap-2 sm:flex-row">
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 focus-within:border-primary-400">
              <i className="ri-search-line w-4 h-4 flex items-center justify-center text-foreground-500" />
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="输入片名、演员、导演或题材"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              {input && (
                <button type="button" onClick={() => setInput('')} aria-label="清空" className="text-foreground-400 transition hover:text-foreground-700">
                  <i className="ri-close-circle-line text-base" />
                </button>
              )}
            </div>
            <button type="submit" className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600">
              搜索
            </button>
          </form>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-foreground-400">热门搜索</span>
            {hotKeywords.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => setParams({ q: word })}
                className="rounded-full bg-background-50 px-2.5 py-0.5 text-[11px] text-foreground-700 transition hover:bg-primary-100 hover:text-primary-800"
              >
                {word}
              </button>
            ))}
          </div>
        </section>

        {keyword && (
          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-foreground-500">搜索关键词</span>
            <span className="rounded-md bg-primary-100 px-2 py-0.5 font-medium text-primary-800">{keyword}</span>
            <span className="text-xs text-foreground-500">共找到 {results.length} 部相关作品</span>
          </div>
        )}

        {keyword && matched.length > 0 && (
          <>
            <div className="mt-4 hidden lg:block">
              <FilterBar genres={allGenres} filters={filters} onChange={(patch) => setFilters((prev) => ({ ...prev, ...patch }))} />
            </div>
            <div className="mt-3 lg:hidden">
              <MobileFilter genres={allGenres} filters={filters} onChange={(patch) => setFilters((prev) => ({ ...prev, ...patch }))} resultCount={results.length} />
            </div>
            <div className="mt-3">
              <FilterSummary filters={filters} onReset={() => setFilters(defaultFilters)} />
            </div>
          </>
        )}

        {!keyword ? (
          <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-12 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-500">
              <i className="ri-search-line text-xl" />
            </span>
            <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">输入关键词开始搜索</h2>
            <p className="mt-2 text-xs text-foreground-500">可以搜索片名、导演、演员或题材，例如“悬疑”“2026”。</p>
          </div>
        ) : results.length === 0 ? (
          <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-50 p-6 py-12 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-500">
              <i className="ri-emotion-sad-line text-xl" />
            </span>
            <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">没有找到与“{keyword}”相关的作品</h2>
            <p className="mt-2 text-xs text-foreground-500">试试更换关键词，或先看看这些热门推荐。</p>
            <div className="mx-auto mt-5 grid max-w-2xl grid-cols-2 gap-3 sm:grid-cols-4">
              {recommendations.map((item, index) => (
                <MediaCard key={item.id} id={item.id} index={index} />
              ))}
            </div>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              <Link to="/category/电影" className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition hover:bg-primary-600">
                浏览电影分类
              </Link>
              <button type="button" onClick={() => setFilters(defaultFilters)} className="whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition hover:border-primary-400">
                重置筛选条件
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            {results.map((item) => (
              <MediaCard key={item.id} id={item.id} layout="row" />
            ))}
          </div>
        )}
      </PortalShell>
    </SiteLayout>
  );
}